import { useEffect, useState } from "react";
import RightTimeApi from "@/API";
import "@style/Admin.scss";
import Enums from "@/enums";
import { toast } from "react-toastify";
import { MdEdit, MdDelete, MdAdd } from "react-icons/md";
import { FaPlusSquare, FaFileDownload } from "react-icons/fa";
import BillableItemModal from "@modals/BillableItemModal";
import InvoiceLineItemModal from "@modals/InvoiceLineItemModal";
import InvoiceDetailModal from "@modals/InvoiceDetailModal";
import Helpers from "@/Helpers";
import { downloadResponseAsFile } from "@/utils/authenticatedFileDownload";

// Invoice History Tab
export default function CompanyInvoicesTab(props) {
  const { tabData, billingData, updateSpinner, updateAccountingData } = props;
  const [selectedInvoiceLineItemId, setSelectedInvoiceLineItemId] = useState(null);
  const [invoiceId, setInvoiceId] = useState(null);

  function handleInvoiceLineItemClick(invoiceLineItemId) {
    setSelectedInvoiceLineItemId(invoiceLineItemId);
  }

  function handleInvoiceLineItemModalClose() {
    setSelectedInvoiceLineItemId(null);
    if (updateAccountingData)
      updateAccountingData();
  }

  function handleInvoiceRowClick(invoice) {
    setInvoiceId(invoice.id);
  }

  function onAddInvoiceAsync() {
    setInvoiceId(0);
  }

  function handleCreateNewLineItem() {
    setSelectedInvoiceLineItemId(0);
  }

  function handleInvoiceDetailModalClose() {
    setInvoiceId(null);
    if(updateAccountingData) 
      updateAccountingData();
  }

  async function downloadInvoiceCsv(exportPath, fallbackFilename) {
    if (!tabData.company?.id) {
      toast.error("Company is required to export invoices");
      return;
    }

    try {
      updateSpinner(true);
      const response = await RightTimeApi.getAsync(exportPath);
      const ok = await downloadResponseAsFile(response, fallbackFilename);
      if (ok) toast.success("CSV exported successfully");
      else toast.error("Failed to export CSV");
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast.error("Error exporting CSV");
    } finally {
      updateSpinner(false);
    }
  }

  async function handleDownloadPdf(e, invoice) {
    e.stopPropagation(); // Prevent row click event
    
    if (!invoice.id || !tabData.company?.id) {
      toast.error("Unable to download PDF - missing invoice or company ID");
      return;
    }

    try {
      updateSpinner(true);
      const response = await RightTimeApi.getAsync(`/Company/${tabData.company.id}/Invoice/${invoice.id}/Pdf`);
      const ok = await downloadResponseAsFile(
        response,
        `invoice_${invoice.invoiceNumber || invoice.id}.pdf`
      );
      if (ok) toast.success("Invoice PDF downloaded successfully");
      else toast.error("Failed to download invoice PDF");
    } catch (error) {
      console.error("Error downloading invoice PDF:", error);
      toast.error("Error downloading invoice PDF");
    } finally {
      updateSpinner(false);
    }
  }

  return (
    <div className="horizontal companyInvoicesPanel">
      <div className="invoiceExportToolbar">
        <button
          type="button"
          className="btn btn-outline exportInvoicesCsvButton"
          onClick={() => downloadInvoiceCsv(
            `/Company/${tabData.company.id}/Invoices/Export/Summary`,
            "invoices-summary.csv"
          )}
        >
          Export Invoices CSV
        </button>
        <button
          type="button"
          className="btn btn-outline exportLineItemsCsvButton"
          onClick={() => downloadInvoiceCsv(
            `/Company/${tabData.company.id}/Invoices/Export/LineItems`,
            "invoice-line-items.csv"
          )}
        >
          Export Line Items CSV
        </button>
      </div>

      <div className="tableWrapper factTable scrollable pendingInvoiceItemsTable">
        <table>
          <caption><span className="title">Pending Invoice Items</span>
            <span
              className="floatRight clickableIcon"
              onClick={handleCreateNewLineItem}
              title="Create New Line Item"
            >
              <FaPlusSquare className="actionIcon plusIcon" />
            </span>
          </caption>
          <thead>
            <tr>
              <th></th>
              <th>Date</th>
              <th>Quantity</th>
              <th>Amount</th>
              <th>Fee Item</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {billingData && billingData.openInvoiceLineItems.length > 0 ? (
              billingData.openInvoiceLineItems.sort((a, b) => new Date(a.chargeDate).getTime() - new Date(b.chargeDate).getTime()).map(item => (
                <tr 
                  key={item.id}
                  onClick={() => handleInvoiceLineItemClick(item.id)}
                  className="clickable"
                  title="Click to edit invoice line item"
                >
                  <td>{item.id}</td>
                  <td>{Helpers.formatDate(item.chargeDate)}</td>
                  <td>{item.quantity}</td>
                  <td>{Helpers.formatCurrency(item.amount)}</td>
                  <td>{item.companyFeeItemName}</td>
                  <td className="left">{item.description}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8} className="center">No pending invoice items found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="tableWrapper factTable scrollable scrollableTableMedium">
        <table>
          <caption>
            <span className="title">Invoice History
            </span><span
              className="actionIcon plusIcon floatRight clickableIcon"
              onClick={() => setInvoiceId(0)}
              title="Create New Invoice"
            >
              <FaPlusSquare />
            </span>
          </caption>
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Date</th>
              <th>Due Date</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Description</th>
              <th>PDF</th>
            </tr>
          </thead>
          <tbody>
            {billingData && billingData.invoices.length > 0 ? (
              billingData.invoices.map((invoice) => {
                console.log('Invoice object:', invoice);
                return (
                  <tr 
                    key={invoice.id}
                    onClick={() => handleInvoiceRowClick(invoice)}
                    className="clickable clickableRow"
                    title="Click to view/edit invoice"
                  >
                    <td>{invoice.invoiceNumber || invoice.number || invoice.invoiceNo || invoice.id || 'N/A'}</td>
                    <td>{Helpers.formatDate(invoice.invoiceDate)}</td>
                    <td>{Helpers.formatDate(invoice.dueDate)}</td>
                    <td className="right">{Helpers.formatCurrency(invoice.totalAmount)}</td>
                    <td>
                      <span className={`status ${invoice.status?.toLowerCase()}`}>{invoice.status}</span>
                    </td>
                    <td>{invoice.description}</td>
                    <td className="center">
                      <FaFileDownload 
                        className="actionIcon downloadIcon clickableIcon"
                        onClick={(e) => handleDownloadPdf(e, invoice)}
                        title="Download PDF"
                      />
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={7} className="center">No invoices found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Invoice Line Item Modal */}
      <InvoiceLineItemModal
        invoiceLineItemId={selectedInvoiceLineItemId}
        companyId={tabData.company?.id}
        invoiceId={null}
        adminPerms={tabData.adminPerms}
        onClose={handleInvoiceLineItemModalClose}
        updateSpinner={updateSpinner}
        onSave={handleInvoiceLineItemModalClose}
      />

      {/* Invoice Detail Modal */}
      {(() => {
        console.log('Modal render check - invoiceId:', invoiceId, 'companyId:', tabData.company?.id);
        return invoiceId !== null;
      })() && (
        <InvoiceDetailModal
          key={`invoice-modal-${invoiceId}`}
          invoiceId={invoiceId}
          companyId={tabData.company?.id}
          onClose={handleInvoiceDetailModalClose}
          billingData={billingData}
          updateSpinner={updateSpinner}
        />
      )}
    </div>
  );
}

